import { X } from '@phosphor-icons/react';
import * as React from 'react';
import { useMemo } from 'react';
import { cn } from '../../utils/cn';
import { useClickOutside } from './hooks/useClickOutside';
import type { SimpleModalProps } from './types';

const getModalContentPlacementClassNames = (
  isOpen: boolean,
  placement?: SimpleModalProps['placement'],
  mobilePlacement?: SimpleModalProps['placement'],
) => {
  const defaultClassNames = cn('h-fit transform duration-200', isOpen && 'fixed', !placement && 'md:w-auto');

  let mobilePlacementClassNames: string;
  switch (mobilePlacement) {
    case 'bottom':
      mobilePlacementClassNames = cn('w-full bottom-0 left-0');
      break;
    case 'top':
      mobilePlacementClassNames = cn('w-full top-0 left-0');
      break;
    default:
      mobilePlacementClassNames = cn('w-[95%] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]');
      break;
  }

  switch (placement) {
    case 'bottom':
      return cn(
        defaultClassNames,
        mobilePlacementClassNames,
        'md:w-full md:bottom-0 md:left-0',
        isOpen ? 'translate-y-0' : 'translate-y-3/4',
      );
    case 'top':
      return cn(
        defaultClassNames,
        mobilePlacementClassNames,
        'md:w-full md:top-0 md:left-0',
        isOpen ? 'translate-y-0' : '-translate-y-3/4',
      );
    default:
      return cn(
        defaultClassNames,
        mobilePlacementClassNames,
        'md:w-fit md:transition-opacity md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%]',
        isOpen ? 'md:opacity-100' : 'md:opacity-0',
      );
  }
};

const SimpleModal: React.FC<SimpleModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  overlayClassName,
  style,
  placement,
  mobilePlacement = 'bottom',
  showRoundedBorders = true,
  showCloseButton = true,
  shouldCloseOnOutsideClick = true,
  shouldDisableScroll = true,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  useClickOutside({
    isOpen,
    onClose,
    contentRef,
    shouldCloseOnOutsideClick,
    shouldDisableScroll,
  });

  const modalContentPlacementClassNames = useMemo(
    () => getModalContentPlacementClassNames(isOpen, placement, mobilePlacement),
    [placement, isOpen],
  );

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'transition-opacity duration-200',
        isOpen ? 'fixed' : 'absolute w-0 h-0 overflow-hidden -left-4 -top-4',
        isOpen ? 'opacity-100' : 'opacity-0',
        'inset-0 z-50 bg-black/80',
        'bg-[--overlay-background-color]',
        overlayClassName,
      )}
      style={style}
    >
      <div
        ref={contentRef}
        className={cn(modalContentPlacementClassNames, showRoundedBorders && 'rounded-lg', 'bg-background', className)}
      >
        {showCloseButton && (
          <button
            type="button"
            onClick={() => onClose?.(false)}
            className={cn(
              'z-10 absolute right-[--modal-close-button-placement-right] top-[--modal-close-button-placement-top] rounded-sm opacity-70',
              'hover:opacity-100 transition-opacity',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'text-[--modal-close-button-color]',
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

export default SimpleModal;
